import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';

import { useTranslation } from 'src/i18n';
import { pushHistory } from 'src/module/navigation/action';
import { DataTableColumnHeader } from 'src/component/ds/ui/dataTableColumnHeader';
import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';
import { DsDataTable } from 'src/component/ds/dataTable/DsDataTable';
import { IconColumnHeader } from 'src/component/ds/dataTable/IconColumnHeader';
import { dsSelectColumn } from 'src/component/ds/dataTable/selectColumn';
import { useDsTable } from 'src/component/ds/dataTable/useDsTable';
import type { DeviceListScope } from 'src/service/api/deviceList';
import type { DeviceListRow } from './deviceListRows';
import useDeviceList from './useDeviceList';
import {
  AlarmCountBadges,
  AlarmsColumnHeader,
  CircuitCell,
  ControllerCell,
  WorkOrdersCountBadge
} from './cells';
import { NoDevicesYet, NoMatchingDevices } from './DeviceListEmpty';

export type DeviceListColumnId =
  'group' | 'connection' | 'area' | 'panel' | 'controller' | 'circuit' | 'alarms' | 'workOrders';

export const DEFAULT_DEVICE_LIST_COLUMNS: readonly DeviceListColumnId[] =
  ['area', 'panel', 'controller', 'circuit', 'alarms', 'workOrders'];

const ALL_COLUMNS: readonly DeviceListColumnId[] = ['group', 'connection', ...DEFAULT_DEVICE_LIST_COLUMNS];

export interface DeviceListProps {
  columns?: readonly DeviceListColumnId[];
  /** Narrows the list, for embedding on a panel or controller screen. */
  scope?: DeviceListScope;
  /** Free-text search, ANDed with the filters and scope. */
  search?: string;
  onViewAllDevices?: () => void;
  className?: string;
}

const col = createColumnHelper<DataTableFeatures, DeviceListRow>();

const UNGROUPED_SORTS = ['alarms', 'workOrders'];

const toSortParam = (sorting: SortingState) =>
  sorting[0] ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}` : 'area';

export default function DeviceList ({ columns: shownColumns = DEFAULT_DEVICE_LIST_COLUMNS, scope, search = '', onViewAllDevices, className }: DeviceListProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { rows, loading, hasMore, onLoadMore, error, filtered } = useDeviceList(toSortParam(sorting), scope, search);

  const grouped = !UNGROUPED_SORTS.includes(sorting[0]?.id);

  const columns = useMemo(() => col.columns([
    dsSelectColumn<DeviceListRow>(),
    // A group or connection always leads the order, so its label never needs repeating.
    col.accessor((r) => (r.level === 'controller' ? r.data.group : undefined), {
      id: 'group',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('deviceList.group')} className='ds-font-semibold ds-text-muted-foreground' />
      ),
      enableSorting: false,
      meta: { levels: ['controller'], hideRepeats: true, className: 'ds-w-[140px] ds-font-semibold' },
    }),
    col.accessor((r) => (r.level === 'controller' ? r.data.connection : undefined), {
      id: 'connection',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('deviceList.connection')} className='ds-font-semibold ds-text-muted-foreground' />
      ),
      enableSorting: false,
      meta: { levels: ['controller'], hideRepeats: true, className: 'ds-w-[180px] ds-font-semibold' },
    }),
    col.accessor((r) => (r.level === 'controller' ? r.data.area : undefined), {
      id: 'area',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('deviceList.area')} />,
      meta: { levels: ['controller'], hideRepeats: grouped, className: 'ds-w-[60px]' },
    }),
    col.accessor((r) => (r.level === 'controller' ? r.data.panel : undefined), {
      id: 'panel',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('deviceList.panel')} />,
      meta: { levels: ['controller'], hideRepeats: grouped, className: 'ds-w-[120px] ds-font-semibold' },
    }),
    col.accessor((r) => r.data.tag, {
      id: 'controller',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('deviceList.controller')} />,
      meta: { levels: ['controller'], className: 'ds-w-[170px]' },
      cell: ({ row }) => (row.original.level === 'controller' ? <ControllerCell data={row.original.data} /> : null),
    }),
    col.accessor((r) => r.data.tag, {
      id: 'circuit',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('deviceList.circuit')} />,
      cell: ({ row }) => <CircuitCell row={row.original} />,
    }),
    col.accessor((r) => r.data.severities, {
      id: 'alarms',
      header: ({ column }) => <AlarmsColumnHeader column={column} />,
      meta: { className: 'ds-w-[220px]' },
      cell: ({ getValue }) => <AlarmCountBadges severities={getValue()} />,
    }),
    col.accessor((r) => r.data.workOrders, {
      id: 'workOrders',
      header: ({ column }) => (
        <IconColumnHeader
          column={column}
          title={t('deviceList.workOrders')}
          icon={<ContentPasteOutlinedIcon />}
        />
      ),
      meta: { className: 'ds-w-[64px]' },
      cell: ({ getValue }) => <WorkOrdersCountBadge count={getValue()} />,
    }),
    col.display({
      id: 'open',
      header: () => <span className='ds-sr-only'>{t('deviceList.open')}</span>,
      meta: { className: 'ds-w-8 ds-text-muted-foreground' },
      cell: () => <ChevronRightIcon fontSize='small' aria-hidden='true' />,
    }),
  ]), [t, grouped]);

  const columnVisibility = Object.fromEntries(ALL_COLUMNS.map((id) => [id, shownColumns.includes(id)]));

  const table = useDsTable({
    data: rows,
    columns,
    state: { sorting, rowSelection, columnVisibility },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
  });

  const scoped = !!(scope?.panelId || scope?.commLoopId || scope?.groupId || scope?.controllerId);
  const isEmpty = !rows.length && !loading && !hasMore && !error;
  if (isEmpty && filtered) {
    return <NoMatchingDevices className={className} />;
  }
  if (isEmpty) {
    return (
      <NoDevicesYet
        scope={scope}
        onViewAllDevices={scoped ? onViewAllDevices : undefined}
        className={className}
      />
    );
  }

  return (
    <DsDataTable
      table={table}
      hasMore={hasMore}
      loading={loading}
      onLoadMore={onLoadMore}
      onRowClick={(row) => dispatch(pushHistory(row.original.data.href))}
      emptyMessage={error ? t('deviceList.loadFailed') : t('deviceList.empty')}
      className={className}
    />
  );
}
